import { type ReactNode, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router";
import type { DataToolResult } from "../../api/hub/open";
import { SourceLink } from "../focus/shared";
import { dateLabel, message, useWorkspaceMe } from "./data";
import "./home.css";
export function WorkDeskHeader({
  title,
  mode,
  description,
  actions,
  children,
}: {
  title: string;
  mode?: string;
  description: string;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  const location = useLocation();
  return (
    <>
      <nav className="home-desk-nav" aria-label="工作桌">
        <Link
          to="/dashboard"
          aria-current={location.pathname === "/dashboard" ? "page" : undefined}
        >
          标准仪表盘
        </Link>
        <Link
          to="/personal-home"
          aria-current={
            location.pathname === "/personal-home" ? "page" : undefined
          }
        >
          我的主页
        </Link>
        <Link
          to="/apps"
          aria-current={
            location.pathname.startsWith("/apps") ? "page" : undefined
          }
        >
          应用中心
        </Link>
      </nav>
      <header className="home-heading">
        <div>
          {mode && (
            <p className="home-eyebrow">
              工作桌 <span className="home-mode">{mode}</span>
            </p>
          )}
          <h1>{title}</h1>
          <p className="home-muted">{description}</p>
          {children}
        </div>
        {actions && <div className="home-actions">{actions}</div>}
      </header>
    </>
  );
}
export function DataState({
  query,
  children,
  empty,
  isEmpty = false,
}: {
  query: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
    data?: unknown;
  };
  children: ReactNode;
  empty?: ReactNode;
  isEmpty?: boolean;
}) {
  if (query.isPending) {
    return (
      <p className="home-empty" role="status">
        正在读取…
      </p>
    );
  }
  if (query.isError && !query.data) {
    return (
      <div className="home-error" role="alert">
        <p>{message(query.error)}</p>
        <button onClick={() => void query.refetch()}>重试</button>
      </div>
    );
  }
  return (
    <>
      {query.isError && (
        <div className="home-error" role="alert">
          <p>当前保留上次结果：{message(query.error)}</p>
          <button onClick={() => void query.refetch()}>重试</button>
        </div>
      )}
      {isEmpty ? (
        <p className="home-empty">{empty ?? "当前没有事项。"}</p>
      ) : (
        children
      )}
    </>
  );
}
export function DataSource({
  result,
  href,
}: {
  result?: DataToolResult<unknown>;
  href?: string;
}) {
  const me = useWorkspaceMe();
  if (!result) return null;
  const links = href ? [{ href, label: "回到业务页面 ↗" }] : result.links;
  return (
    <details className="home-source-disclosure">
      <summary>数据来源</summary>
      <footer className="home-source">
        <span>数据更新于 {dateLabel(result.asOf, me.data?.data.timezone)}</span>
        {links.map((link, i) => (
          <SourceLink key={i} href={link.href}>
            {link.label}
          </SourceLink>
        ))}
      </footer>
    </details>
  );
}
export function MoreButton({
  query,
}: {
  query: {
    hasNextPage?: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => unknown;
    isFetchNextPageError: boolean;
    error: unknown;
  };
}) {
  return (
    <>
      {query.isFetchNextPageError && (
        <p role="alert" className="home-error">
          下一页未读到：{message(query.error)}
        </p>
      )}
      {query.hasNextPage && (
        <button
          className="home-more"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage
            ? "正在读取更多…"
            : query.isFetchNextPageError
              ? "重试下一页"
              : "查看更多"}
        </button>
      )}
    </>
  );
}
export function HomePanel({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const panel = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab" || !panel.current) return;
      const controls = [
        ...panel.current.querySelectorAll<HTMLElement>(
          "button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href]",
        ),
      ];
      const first = controls[0],
        last = controls.at(-1);
      if (
        e.shiftKey &&
        (document.activeElement === first ||
          document.activeElement === panel.current)
      ) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return (
    <div className="home-panel-shade">
      <aside
        ref={panel}
        className="home-panel"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header>
          <h2>{title}</h2>
          <button onClick={onClose}>关闭</button>
        </header>
        {children}
      </aside>
    </div>
  );
}
